import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";

interface DeviceSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  /** Debounce delay in ms for propagating input changes upstream. Defaults to 250ms. */
  debounceMs?: number;
}

export const DeviceSearch = ({ searchQuery, onSearchChange, debounceMs = 250 }: DeviceSearchProps) => {
  // Local state keeps the input responsive while we debounce upstream updates.
  const [value, setValue] = useState(searchQuery);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync when the prop changes externally (e.g. URL clear, navigation).
  useEffect(() => {
    setValue(searchQuery);
  }, [searchQuery]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const schedule = (next: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearchChange(next), debounceMs);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    schedule(e.target.value);
  };

  const handleClear = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setValue("");
    onSearchChange(""); // immediate
  };

  return (
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
      <Input
        type="text"
        placeholder="Search devices..."
        value={value}
        onChange={handleChange}
        className="pl-9 w-full rounded-full border-gray-200"
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
          onClick={handleClear}
          aria-label="Clear search"
        >
          <X className="h-4 w-4 text-gray-500" />
        </Button>
      )}
    </div>
  );
};
